import React, {ReactNode} from 'react'
import {CHARGE_UNIT, HOURS, HOURS_24, UNIT_OF_BUSINESS_SCHEDULE, UNIT_TIME, VALID_OR_NOT} from '../../../../CodeMaster'
import {Box} from '@mui/material'
import {TimeTableItem, TimeTableTime} from './BusinessScheduleItemInterfaces'
import {formatNumberWithCommas} from '../../../../formatters/formatNumberWithCommas'
import {Nullable} from '../../../../app/TypeHelpers'
import SchedulesViewDetailItem, {ScheduleViewDetailItemBase} from './SchedulesViewDetailItem'
import {compare, formatTimeString} from './BusinessScheduleTimeHelper'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import equal from 'fast-deep-equal'

interface Props {
    items: TimeTableItem[]
    unitOfBusinessSchedule: UNIT_OF_BUSINESS_SCHEDULE
    onDeleteClick?: (id: string) => void
}

const SchedulesViewDetail: React.FC<Props> = React.memo(props => {

    const renderScheduleItems = (): ReactNode => {
        return props.items.map((item, itemIndex) => {
            const {
                startTime, endTime, businessScheduleUnit, charge, chargeUnit,
                chargeUnitTime, changedCharge, changedChargeApplyStartTime, changedChargeApplyStartTimeUnit,
                changedChargeUnit, changedChargeUnitTime,
            } = item

            const multiDaySchedule: boolean =
                businessScheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS
                || businessScheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE

            const scheduleString = createScheduleString(businessScheduleUnit, startTime, endTime)
            const chargeMainString = createChargeMainString(charge, chargeUnitTime, chargeUnit)
            const chargeSubString = createChargeSubString(
                changedCharge,
                changedChargeApplyStartTime,
                changedChargeApplyStartTimeUnit,
                changedChargeUnit,
                changedChargeUnitTime,
            )

            let detailString: string
            if (item.joinPrevDaysSchedule === VALID_OR_NOT.VALID) {
                detailString = '前日の営業スケジュールと連結（日またぎ設定）'
            } else {
                detailString = `${chargeMainString}${chargeSubString}`
            }

            return (
                <div key={item.id}>
                    {
                        itemIndex !== 0 &&
                        <Box key={`mt${itemIndex}`} mt={'2px'}/>
                    }
                    <SchedulesViewDetailItem
                        key={item.id}
                        id={item.id}
                        multiple={multiDaySchedule}
                        title={`${scheduleString}`}
                        detail={detailString}
                        onDelete={props.onDeleteClick}
                    />
                </div>
            )
        })
    }

    const renderNoSchedules = (): ReactNode => {
        const groupName: string = props.unitOfBusinessSchedule.label
        return <ScheduleViewDetailItemBase
            icon={<EventBusyIcon/>}
            action={<Box width={'48px'} height={'48px'}/>}
            title={`${groupName}には営業スケジュールがありません`}
            detail={'必要に応じて営業スケジュールを登録ください。'}
        />
    }

    const noScheduleItem: boolean = (props.items.length === 0)
    return (
        <Box padding={'5px'}>
            {(noScheduleItem) ? renderNoSchedules() : renderScheduleItems()}
        </Box>
    )
}, (prevProps, nextProps) => (prevProps === nextProps || equal(prevProps, nextProps)))

const createChargeStringIncludeFree = (charge: number): string => {
    return (charge === 0) ? '無料' : `${formatNumberWithCommas(charge)}円`
}
const createChargeUnitTimeStringIncludeFix = (
    unitTime: Nullable<number>,
    unit: Nullable<CHARGE_UNIT>,
): string => {
    if (unit === null) {
        return ''
    }
    return (unit === CHARGE_UNIT.FIXED) ? '固定料金' : `${unitTime}${unit.label}`
}

const createChargeSubString = (
    changedCharge: Nullable<number>,
    changedChargeApplyStartTime: Nullable<number>,
    changedChargeApplyStartTimeUnit: Nullable<UNIT_TIME>,
    changedChargeUnit: Nullable<CHARGE_UNIT>,
    changedChargeUnitTime: Nullable<number>,
): string => {
    if (changedCharge === null ||
        changedChargeApplyStartTime === null ||
        changedChargeApplyStartTimeUnit === null
    ) {
        return ''
    }
    const chargeString: string = createChargeStringIncludeFree(changedCharge)
    const chargeUnitTimeString: string = createChargeUnitTimeStringIncludeFix(changedChargeUnitTime, changedChargeUnit)

    return `（${changedChargeApplyStartTime}${changedChargeApplyStartTimeUnit.label}後から${chargeString}/${chargeUnitTimeString}）`
}

const createChargeMainString = (
    charge: Nullable<number>,
    chargeUnitTime: Nullable<number>,
    chargeUnit: Nullable<CHARGE_UNIT>,
): string => {
    if (charge === null) {
        return ''
    }
    const chargeString: string = createChargeStringIncludeFree(charge)
    const chargeUnitTimeString: string = createChargeUnitTimeStringIncludeFix(chargeUnitTime, chargeUnit)

    return `${chargeString}/${chargeUnitTimeString}`
}

const createScheduleString = (
    scheduleUnit: UNIT_OF_BUSINESS_SCHEDULE,
    startTime: Nullable<TimeTableTime<HOURS_24>>,
    endTime: Nullable<TimeTableTime<HOURS | HOURS_24>>,
): string => {
    if (scheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE) {
        return '24時間'
    }
    if (startTime === null || endTime === null) {
        return ''
    }
    const everyDayString: string = (scheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS) ? '毎日' : ''
    const isNextDay: boolean = (compare(startTime, endTime) > 0)
    const nextDayString: string = (isNextDay) ? '翌' : ''

    return `${everyDayString}${formatTimeString(startTime)}〜${nextDayString}${formatTimeString(endTime)}`
}

SchedulesViewDetail.displayName = 'SchedulesViewDetail'
export default SchedulesViewDetail
